import * as React from 'react'

import { hasAuthParams, useAuth } from 'react-oidc-context'
import { Navigate, useLocation, useParams } from 'react-router-dom'
import { isGuid } from '@/lib/isGuid'
import { isUnusableServiceCompany } from '@/app/constants/companyType'
import { useQueryCompanies } from '@/modules/shared/query-client'
import { ROUTES } from 'app/constants/routes'
import { useUser } from '@/modules/shared/context/UserContext'
import { currentUrlState } from '@/config'

const LoadingSpinner = () => (
  <div className="tw-flex tw-items-center tw-justify-center tw-h-screen tw-w-full">
    <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-blue-500"></div>
  </div>
)

export const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth()

  // Redirect w efekcie (nie w renderze) i tylko raz — bez dodatkowego
  // clearLoginInfo, które równolegle odpalało własny signinSilent/redirect.
  const shouldRedirect =
    !auth.isLoading &&
    !auth.isAuthenticated &&
    !auth.activeNavigator &&
    !hasAuthParams()

  React.useEffect(() => {
    if (shouldRedirect) {
      // url_state → po zalogowaniu /postLogin wróci na tę stronę.
      auth.signinRedirect({ url_state: currentUrlState() })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRedirect])

  if (!auth.isAuthenticated) return <LoadingSpinner />
  return <>{children}</>
}

// Konto usługowe bez bazy ma tylko te ekrany; każdy inny (dashboard, moduły) odpowiada 500.
const SERVICE_COMPANY_SECTIONS = ['serviceAccount', 'my-subscription', 'profile']

export const RequireValidCompanyId: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { companyId } = useParams<{ companyId: string }>()
  const location = useLocation()
  const companies = useQueryCompanies({ includeDeleted: false })
  if (!companyId || !isGuid(companyId)) {
    return <Navigate to={ROUTES.CompanySelection.NoCompany} replace />
  }
  // Czekamy na listę: bez niej ekran firmy zdążyłby wysłać zapytania, które dla konta
  // usługowego kończą się 500 i oknem „brak bazy”.
  if (companies.isLoading) return <LoadingSpinner />
  const company = companies.data?.find(
    (c) => String(c.id).toLowerCase() === companyId.toLowerCase(),
  )
  const section = location.pathname.split('/')[4]
  if (isUnusableServiceCompany(company) && !SERVICE_COMPANY_SECTIONS.includes(section)) {
    return <Navigate to={`/et/company/${companyId}/serviceAccount`} replace />
  }
  return <>{children}</>
}

export const RequireGlobalAdmin: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { accountData, isLoading } = useUser()
  // accountData is undefined until /config-api/user resolves. Don't decide yet.
  if (isLoading || !accountData) return <LoadingSpinner />
  if (!accountData.globalAdmin) {
    return <Navigate to={ROUTES.CompanySelection.NoCompany} replace />
  }
  return <>{children}</>
}

